import { createFileRoute, redirect } from "@tanstack/react-router";
import { Inbox, Send, Users } from "lucide-react";
import { z } from "zod";
import { usePublicConfig } from "@/api/queries/config";
import { FriendOffersList, FriendRequestsList, FriendsList } from "@/components/friends";
import { HotFlirtsSlider } from "@/components/shared";
import { Tabs } from "@/components/ui/Tabs";
import { usePageMeta } from "@/hooks/use-page-meta";

const TAB_VALUES = ["list", "requests", "sent"] as const;
type TabValue = (typeof TAB_VALUES)[number];

const paginationSchema = z.object({
	page: z.coerce.number().optional(),
});

const tabs = [
	{ key: "list", label: "Freunde", icon: <Users size={16} /> },
	{ key: "requests", label: "Anfragen", icon: <Inbox size={16} /> },
	{ key: "sent", label: "Gesendet", icon: <Send size={16} /> },
];

function isValidTab(tab: string): tab is TabValue {
	return (TAB_VALUES as readonly string[]).includes(tab);
}

export const Route = createFileRoute("/_app/friends/$tab")({
	validateSearch: paginationSchema,
	beforeLoad: ({ params }) => {
		if (!isValidTab(params.tab)) {
			throw redirect({ to: "/friends/$tab", params: { tab: "list" } });
		}
	},
	component: FriendsTabPage,
});

function FriendsTabPage() {
	const { tab } = Route.useParams();
	const { page = 1 } = Route.useSearch();
	const navigate = Route.useNavigate();
	const { data: config } = usePublicConfig();

	usePageMeta({
		title: config ? `Freunde - ${config.seo.defaultTitle}` : undefined,
	});

	const handleTabChange = (newTab: string) => {
		navigate({
			to: "/friends/$tab",
			params: { tab: newTab },
			search: { page: undefined },
			replace: true,
		});
	};

	const handlePageChange = (newPage: number) => {
		navigate({
			search: (prev) => ({ ...prev, page: newPage }),
			replace: true,
		});
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	return (
		<div className="space-y-6">
			<h1 className="text-2xl font-bold text-text">Freunde</h1>
			<Tabs tabs={tabs} activeKey={tab} onTabChange={handleTabChange} />
			<div>
				{tab === "list" && <FriendsList page={page} onPageChange={handlePageChange} />}
				{tab === "requests" && <FriendRequestsList page={page} onPageChange={handlePageChange} />}
				{tab === "sent" && <FriendOffersList page={page} onPageChange={handlePageChange} />}
			</div>
			<HotFlirtsSlider />
		</div>
	);
}
